import { Pagamento } from './Pagamento'
import { Categoria, Estacao, FormProdutoSimplesFilho, Genero, Marca, ProdutoFilho, Tag } from '@/models/Produto'
import { Cliente, ClienteDaVenda, EnderecoAdicional, TipoDeCliente } from '../Cliente'
import { PontoDeVenda } from '../PontoDeVenda'
import { SiglaUf } from '../Endereco'
import { RegimeFiscal } from '../fiscal'
import type { CST } from '../fiscal/Imposto'
import type { Origem } from '../fiscal/Origem'
import { AmbienteFiscal, SerieFiscal } from '../SerieFiscal'
import { CupomDeDesconto } from '../ConfiguracaoDeCupom'
import { PedidoDeVenda, SituacaoDoPedidoDeVenda } from './PedidoDeVenda'
import { Operador } from '@/models/venda/Operador'
import { UsuarioBase } from '../usuario'
import { Fornecedor } from '../Fornecedor'
import { MotivoCancelamento } from '../MotivoCancelamento'
import { ValePresente } from '../views/ValePresente'
import { Loja, LojaPublica } from '../Loja'

export interface Venda extends VendaBase {
	itens: ItemDaVenda[]
	cliente: Cliente | null
	pontoDeVenda: PontoDeVenda | null
	vendedor: UsuarioBase[] | null
	vendaOrigem: string | null
	vendaIdParaCancelar?: string | null
	representanteComercial: UsuarioBase | null
	liberarCrediario: boolean
	isOrcamento?: boolean
	isTroca: boolean
	idPreVenda?: string
	autenticandoParaLiberarVendaSemEstoque: boolean | false
	numeroTicket: string
	existeValePresente: boolean | false
	loja?: string | undefined | null
}

export interface VendaComPedido extends Venda {
	pedido: PedidoDeVenda
}

export interface InformacoesDeTransporte {
	modalidadeDeFrete: ModalidadeDeFrete
	volume: number | null
	especie: string | null
	pesoLiquido: number | null
	pesoBruto: number | null
	fornecedor: Fornecedor | null
	valorFrete: number | null
	codigoRastreio: string | null
	tipoDeFrete: string | null
}

export type ModalidadeDeFrete =
	| 'Por Conta do Emitente (CIF)'
	| 'Por Conta do Destinatário (FOB)'
	| 'Por Conta de Terceiros'
	| 'Sem Frete'

export interface VendaParcial {
	informacoesDeTransporte?: InformacoesDeTransporteParcial
	situacao?: Venda['situacao']
	temDevolucao?: Venda['temDevolucao']
	cpfDoCliente?: Venda['cpfDoCliente']
	prazoDevolucao?: Venda['prazoDevolucao']
	volumes?: Venda['volumes']
	produtosVerificados: Venda['produtosVerificados']
	vendaOrigem?: Venda['vendaOrigem']
}

export interface InformacoesDeTransporteParcial {
	modalidadeDeFrete?: InformacoesDeTransporte['modalidadeDeFrete']
	volume?: InformacoesDeTransporte['volume']
	especie?: InformacoesDeTransporte['especie']
	pesoLiquido?: InformacoesDeTransporte['pesoLiquido']
	pesoBruto?: InformacoesDeTransporte['pesoBruto']
	fornecedor?: InformacoesDeTransporte['fornecedor']
}

export interface VendaComReferencias extends ResumoDaVenda {
	itens: ItemDaVendaComReferencias[]
	motivoCancelamento: MotivoCancelamento | null
	dataHoraVendaCriada: Date
	possuiNotaNaoEletronica: boolean
	notaDeTransferenciaComplementarPendente: boolean
}

export interface ResumoDaVenda extends VendaBase {
	pontoDeVenda: PontoDeVendaDaVenda
	vendaOrigem?: VendaOrigem
	cliente: ClienteDaVenda
	vendedor?: Operador[]
	total: number
	quantidade?: number
	situacaoPedido?: string
	loja?: string
	representanteComercial?: Operador
	liberarCrediario: boolean
	isConsignado: boolean | false
	isDemonstracao: boolean | false
	isVendaReaberta: boolean | false
	informacoesDeTransporte: InformacoesDeTransporte
	chaveDanfeExterna: string | null
	identificadorExterno?: string
	possuiPagamentoTefCreditado?: boolean
	possuiPagamentoTefCancelado?: boolean
	modalidade?: ModalidadeDeVenda
	identificadorExterno2?: string
	numeroTicket: string | null
	isDevolucaoValePresente: boolean
}

export interface VendaBase {
	id: string
	pagamentos: Pagamento[]
	desconto: Desconto | null
	dataHora: Date | null
	estado: SiglaUf | null
	regimeFiscal: RegimeFiscal
	descontoRateado: number
	cpfDoCliente: string | '' | null
	tipoDeCliente: TipoDeCliente | null
	exibirCpfNaNota: boolean
	identificador?: string
	tipoDeTransacao: TipoDeTransacaoDeVenda
	notas: NotaDaVenda[]
	situacao: SituacaoDeVenda
	resumoDaVendaOrigem: ResumoDaVendaOrigem | null
	pedido: PedidoDeVenda | null
	troco: number
	cuponsDeDesconto: CupomDeDesconto[]
	informacoesDeTransporte: InformacoesDeTransporte
	temDevolucao: boolean
	prazoDevolucao: string | null
	itens: ItemDaVendaBase[]
	duplicadoDe: ReferenciaDeVenda | null
	volumes: string[]
	totalDeTaxa: number
	isConsignado: boolean | false
	isDemonstracao: boolean | false
	isVendaReaberta: boolean | false
	tipoNota: TipoNota
	retornoConsignadoFornecedor?: boolean
	tipoNotaCredito?: TipoNotaCredito | null
	tipoNotaDebito?: TipoNotaDebito | null
	loja?: string | null
	serie: SerieFiscal | null
	isBrinde: boolean
	valorDeCashbackAplicado: number
	produtosVerificados: boolean
	cashbackAplicado: boolean | false
	isPresente: boolean
	infoAdicionais: string | null
	taxaDeServico: number
	modalidadeDeVenda: ModalidadeDeVenda | null
	demonstracaoConvertida: boolean
	consignacaoConvertida: boolean
	valesPresentes: ValePresente[]
	existeValePresente: boolean | false
	enderecoAdicional: EnderecoAdicional | null
	possuiNotaNaoEletronica: boolean | false
	isOperacaoPresencial: boolean | true
	canalDeVendas?: CanaldeVendas
	isPedidoMarketplace: boolean | false
	avaliacaoNps?: AvaliacaoNps
	isCopia: boolean | false
	possuiRelacaoComVitrineVirtual?: boolean | false
	numerosDoSorteio: RelacaoVendaENumerosDoSorteio[]
	carrinhoId?: string | null
	tabelaDePrecoManualId?: string | null
	percentualMaximo?: number | null
	descontoBase: number | null
}

export interface PontoDeVendaDaVenda extends
	Pick<PontoDeVenda,
		| 'id'
		| 'identificador'
		| 'nome'
		| 'isHomologacao'
		| 'serieFiscal'
		| 'imprimeReciboNoOrcamento'
		| 'loja'
	> { }

export type VendaOrigem = {
	readonly id: string
	situacao: Venda['situacao']
	identificador: Venda['identificador']
	notas: NotaDaVenda[]
	isConsignado: boolean
	isDemonstracao: boolean
	chaveDanfeExterna: string | null
}

/** Carimbo fiscal opcional aplicado ao incluir o item (ex.: digitação manual na emissão de notas). */
export interface CamposFiscaisManuaisDigitacao {
	cstIcms: CST | null
	aliquotaIcms: number
	cstPis: CST | null
	aliquotaPis: number
	cstCofins: CST | null
	aliquotaCofins: number
	codigosDeBeneficio: string
	cfop: string
	reduzIcms: boolean
	incluirFrete: boolean
	reduzDifal: boolean
	zerarBaseCalculoIcms: boolean
	utilizarCstPisCofinsOrigem: boolean
	reduzImpostosBcIBSCBS: boolean
	cstIBSCBS: CST | null
	codigoClassificacaoTributaria: string | null
	aliquotaIBS: number
	aliquotaCBS: number
	aliquotaFcp: number
	aliquotaMva: number
	codigoCreditoPresumido: string
	aliquotaCreditoPresumido: number
	aliquotaDifICMS: number
	cstIpi: CST | null
	aliquotaIpi: number
	origem: Origem | null
}

export interface ItemDaVendaComReferencias extends ItemDaVendaBase {
	produto: ProdutoFilho
	motivoCancelamento: MotivoCancelamento | null
	loadingItem: boolean | false
	cancelando: boolean | false
	urlImagem: string | string[] | null
	identificadorVendaOrigem: string | null
}

export interface ResumoDaVendaOrigem extends Pick<Venda, 'cliente' | 'cpfDoCliente' | 'id' | 'vendedor'> { }

export interface ReferenciaDeVenda extends Pick<Venda, 'id' | 'identificador'> { }

export interface NotaDaVenda {
	serie: string
	nnf: string
	cstat: string
	chNFe: string
	ambiente: AmbienteFiscal
	modelo: ModeloNF
	dhEmi: string
	codigoDePostagem: string | null
	ruaDest: string
	numeroEnderecoDest: string
	complementoDest: string
	barirroDest: string
	cidadeDest: string
	ufDest: string
	cepDest: string
}

export type ModeloNF = 'NF-e' | 'NFC-e'

export interface ItemDaVenda extends ItemDaVendaBase {
	motivoCancelamento: MotivoCancelamento | null
	produto: FormProdutoSimplesFilho
	possuiDescontoPorTabela: boolean
	percentualDescontoTabelaPreco: number
	isTotalmenteTrocado: boolean | false
	isDaVendaReaberta: boolean | false
	autenticadoParaLiberarProdutoSemEstoque: boolean | false
	quantidadeLida: number | 0
	itemVendaNaoEletronica?: boolean | false
	isDeOutraLoja: boolean | false
	notaNaoEletronica?: boolean | false
	isPresente?: boolean | false
	usuarioIdLiberacaoVendaSemEstoque?: string
	selecionadoViaVitrineVirtual?: boolean | false
	camposFiscaisDigitacaoManual?: CamposFiscaisManuaisDigitacao | null
	skuPai: string | null
	selecionadoViaOfertaRelacionada?: boolean | false
}

export interface ItemDaVendaBase {
	id: string
	preco: number
	descontoVarejo: number | null
	precoDeCusto: number
	quantidade: number
	desconto: Desconto
	idConjunto: string | null | undefined
	percentualCashback: number
	diasParaEfetivarCashback: number
	isBrinde: boolean
	idItemNotaOrigem: string | null
	chaveNotaOrigem: string | null
	diasParaExpirarCashback: number
	valorDescontoRateadoDoItem: number
	itemDeDevolucao: boolean
	isCompra: boolean
	idItemOrigem: string
	vendedor: UsuarioBase | null
	vendaOrigemDoItem: string | null
	valorFreteRateado: number,
	isItemReferenteATroca: boolean | false
	precoDeCustoReal: number
	selecionadoViaVitrineVirtual?: boolean | false
	selecionadoViaOfertaRelacionada?: boolean | false
}

export interface ItemParaControleDeQuantidade {
	produto: FormProdutoSimplesFilho
	quantidadeLida: number | 0
}

export interface DadosParaReaberturaMultiplaDeVendas {
	id: string
	identificador: string
	possuiNotaNaoEletronica: boolean | false
	itens: ItemDaVenda[]
	vendaNaoPossuiNota: boolean | false
}

export interface Desconto {
	isPercentual: boolean
	valor: number
}

export interface FormDesconto extends Omit<Desconto, 'valor'> {
	valor: number | null | ''
}

export interface ParametrosDeCalculo {
	estado: SiglaUf
	regimeFiscal: RegimeFiscal
}

export type TipoDeTransacaoDeVenda = | 'Venda' | 'Devolução' | 'Transferência' | 'Transferência com redução' | 'Outros' | 'Conserto' | 'Ativo' | 'Consumo' | 'Uniforme' | 'Complementar' | 'Devolução de Transferência'

export type TipoDeTransacaoDeRegraFiscal = 'Venda' | 'Devolução' | 'Transferência' | 'Uniforme' | 'Consignado' | 'Demonstração' | 'Brinde' | 'Outros' | 'Conserto' | 'Ativo' | 'Consumo' | 'Não Configuradas' | 'Conversão'

export type TipoNota = 'Entrada' | 'Saída'

/**
 * Tipo da Nota de Crédito (tpNFCredito, NT 2025.002) que a emissão de notas emite: 03 e 06 saem sobre a
 * devolução de entrada, 04 sai pela operação Nota de Crédito (venda Complementar, sem estoque).
 */
export type TipoNotaCredito = '03' | '04' | '06'

export const DESCRICAO_DO_TIPO_NOTA_CREDITO: Record<TipoNotaCredito, string> = {
	'03': 'Nota de Crédito - retorno por recusa total na entrega',
	'04': 'Nota de Crédito - redução de valores',
	'06': 'Nota de Crédito - retorno por recusa parcial na entrega',
}

/**
 * Tipo da Nota de Débito (tpNFDebito, NT 2025.002) que a emissão de notas emite: 04 sai pela operação Nota de Débito
 * sobre a multa e os juros recebidos (venda Complementar de saída, sem estoque).
 */
export type TipoNotaDebito = '04'

export const DESCRICAO_DO_TIPO_NOTA_DEBITO: Record<TipoNotaDebito, string> = {
	'04': 'Nota de Débito - multa e juros',
}

export type TipoEntradaSaidaRegraFiscal = 'Entrada' | 'Saída' | 'Não Configuradas'

export type SituacaoDeVenda = 'Aberta' | 'Cancelada' | 'Concluida' | 'Com Erro'

export interface ParametrosDeBuscaDeVendaPorIdentificador {
	identificador?: string
	isBuscaParaDevolucao?: boolean
	lojaId?: string
	grupoEconomicoId?: string
	vendasConsignadaDemonstracao?: boolean
}

export interface ParametrosDeBuscaDeVendaPorNotaFiscal {
	tipoAmbiente?: AmbienteFiscal
	lojaId?: string
	numeroNota?: string
	serie?: string
}

export interface ParametrosDeBuscaDeVendaPorProduto {
	identificadorProduto?: string
	isBuscaParaDevolucao?: boolean
	lojaId?: string
	vendasConsignadaDemonstracao?: boolean
}

export type ParametrosDeBuscaDeVenda = ParametrosDeBuscaDeVendaPorIdentificador | ParametrosDeBuscaDeVendaPorNotaFiscal | ParametrosDeBuscaDeVendaPorProduto | undefined

export interface FiltroDeVenda {
	busca: string
	lojaId: string
	datas: [] | [string | null, string | null]
	horas: [string | null, string | null]
	cliente: Cliente | string | null
	pdvId: string
	numeroDaNota: string
	serieFiscal: string
	exibeVendasComErros: boolean
	identificadorExterno: string | null
	ambientes: ('Produção' | 'Homologação')[]
	identificador: string
	vendasComOrcamento: boolean
	vendasComPagamentoCancelado: boolean
	origem: string[] | null
	vendasConsignadaDemonstracao: boolean
	vendedorId: string | null
	canalDeVendasId: string | null
	somenteVenda?: boolean
}

export interface FiltroDePedido {
	lojaId: string | null
	cliente: Cliente | null
	identificador: string | null
	identificadorExterno: string | null
	situacoes: SituacaoDoPedidoDeVenda[]
	datas: [] | [string | null, string | null]
	possuiFrete: boolean
	retiradaNaLoja: boolean
	canalDeVendasId: string | null
	isPedidoDevolucao: boolean
	exibeVendasComErros?: boolean
}

export interface TotalizadoresDeVenda {
	total: number | null
	totalLiquido: number | null
	troco: number | null
	quantidade: number | null
	desconto: number | null
}

export interface BandeirasDeCartao {
	id: string
	nome: string
}

export interface Adquirente {
	id: string
	razaoSocialOuNome: string
	cnpjOuCpf: string
	ativo: boolean
	codigoSitef: string | null
}

export interface ModalidadeDeVenda {
	id: string
	modalidade: string
	origem: 'Presencial' | 'Devolução' | 'Demonstração' | 'Ecommerce' | 'Link' | 'Outro' | 'Vitrine Virtual'
	lojaId: string
	sequencial: number
	modalidadePadrao: boolean
}

export interface TipoDeOrigemDeVenda {
	isDevolucao: boolean
	isDemonstracao: boolean
	isVenda: boolean
}

export interface TicketTiflux {
	ticket_number: string
	title: string
	attend_expiration: string
	is_revised: boolean
	requestor_email: string
	followers: string
	solve_expiration: string
	solve_in_time: string
	created_by_way_of: string
	created_at: string
	updated_at: string
	pause_sla_status: object
	attend_sla: string
	client: {
		id: number
		name: string
	}
	desk: {
		active: boolean
		desk_with_sla: boolean
		display_name: string
		id: number
		name: string
	}
	responsible: {
		email: string
		id: number
		name: string
	}
	priority: {
		name: string
	}
	service_catolog: object
	stage: {
		expiration: Date
		id: number
		name: string
	}
	status: {
		id: number
		name: string
	}
	rating: number
	entities: {}
	parent_ticket: {
		desk_id: number
		responsible_id: number
		stage_id: number
		status_id: number
		ticket_number: number
		title: string
	}
	children_ticket: Array<any>[]
	description: string
	closed_by: object
	closed_stage: object
	closed_status: object
	custom_fields: Array<any>[]
	equipment: object
	first_appointment: string
	priorty_change_reason: string
	reopen_count: number
	requestor: {
		email: string
		id: number
		name: string
		telephone: {
			number: string
		}
	}
	files: object
	revised_limit_time: number
	revised_time: number
	stopped: boolean
	worked_hours: string
	is_closed: boolean
	closed_ticket_total_spent_solving: string
}

export interface ValidaDevolucaoForm {
	idVendaOrigem: string,
	produtoId: string,
	quantidade: string,
}

export interface VendaDemonstracaoResumida {
	id: string;
	modalidade: string;
	total: number;
	loja: number;
	pdv: string;
	dataHora: Date;
	cliente: string;
	tipoCliente: string;
	notaFiscal: string;
	status: string;
	hierarquia: string[];
	isDemonstracao: boolean;
	cnpjOuCpf: string;
	modeloNF: string;
	possuiNotaNaoEletronica: boolean;
	ambiente: String;
	temDevolucao: boolean
	situacao: string
	possuiPagamentoTefCreditado: boolean
	possuiPagamentoTefCancelado: boolean
	limiteDeDiasParaCancelarNota: number
	dataEmissaoDaNota: Date
	identificador: string
}

export interface FiltroDeVendasSemNota {
	vendasId: string[]
}

export interface EtapaDaVenda {
	estado: string
	vendas: Venda[]
}

export interface CanaldeVendas {
	id: string | null
	nome: string
	imageURL: string
}

export interface ParametrosDeAtualizacaoCanaldeVendas {
	id: string | null
	nome: string
	file: File | null
}
export interface Promocao {
	id: string
	nome: string
	descricao: string
	ativa: boolean
	tipoDePromocao: string
	dataInicio: string
	dataFim: string
	todosOsProdutos: boolean
	beneficio: string
	aplicarPorItem: boolean
	acumulativa: boolean
	limitePorCliente: number
	limiteTotal: number
	descontoMax: number
	aplicavelUmaVezPorDia: boolean
	aplicavelUmaVezPorMes: boolean
	badges?: string[]
	tags: Tag[]
	categorias: Categoria[]
	marcas: Marca[]
	skus: String[]
	estacoes: Estacao[]
	generos: Genero[]
	lojas: Loja[]
	tiposDeCliente: TipoDeCliente[]
	regrasDeAplicacaoPromocao: RegraDeAplicacaoPromocao[]
	promocoesDescontoProgressivo: PromocaoDescontoProgressivo[]
	sorteios: PromocaoDeSorteio[]
	obrigatorioCliente: boolean
}

export interface RegraDeAplicacaoPromocao {
	id: string
	promocao: string
	tipoDeGatilho: string
	operadorCondicao: string
	valor: string
	ordem: number
	condicaoLogica: string
}

export interface PromocaoDescontoProgressivo {
	id: string
	quantidadeDePecas: number
	percentualDeDesconto: number
	promocao: string
}

export interface PedidoListagem {
	id: string
    identificador: string
    identificadorExterno: string
    loja: string
    dataHora: Date
    cpfDoCliente: string
    notas: NotaDaVenda[]
    ultimoStatus: string
    total: number
    situacaoPedido: string
    modalidadeFrete: string
    totalDeTaxa: number
    canalDeVendas: string
    valorFrete: number
}

export interface AvaliacaoNps {
	id: string
	vendaId: string
	lojaId: string
	link: string
	status: StatusAvaliacaoNps
	dataExpiracao: Date
	respostas: AvaliacaoNpsResposta[]
}

export interface AvaliacaoNpsResposta {
	id: string
	avaliacaoNpsId: string
	tipo: TipoAvaliacaoNps
	resposta: string | null
	nota: number | null
	pergunta: string | null
}

export type StatusAvaliacaoNps = 'Pendente' | 'Enviado' | 'Concluido' | 'Expirado' | 'Cancelado'

export type TipoAvaliacaoNps = 'Geral' | 'Atendimento' | 'Produtos' | 'Preços e condições' | 'Entrega e retirada' | 'Motivo' | 'Sugestão de melhoria'
export interface PromocaoDeSorteio {
	id: string
	valorMinimoDeCompra: number
	quantidadeDeSorteios: number
	promocao: string
}

export interface SorteioSelecionadoForm {
	promocaoId: string
	quantidadeSorteada: number
	nomePromocao: string
	valorAplicavel: number
}

export interface RelacaoVendaENumerosDoSorteio {
	id: string
	vendaId: string
	promocaoId: string
	numeroSorteado: string
}
